import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { pushNotification, testPushNotification, getRecentSounds } from '../services/api';
import NotificationPreview from './NotificationPreview';

let extraFieldSeq = 0;
const newExtraField = () => ({ id: ++extraFieldSeq, key: '', value: '' });

const extraFieldsToObject = (fields) =>
  fields.reduce((acc, { key, value }) => {
    const k = key.trim();
    if (k) acc[k] = value;
    return acc;
  }, {});

const SendNotification = () => {
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    sound: '',
    image: null,
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [recentSounds, setRecentSounds] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [testToken, setTestToken] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [extraFields, setExtraFields] = useState([]);

  useEffect(() => {
    fetchRecentSounds();
  }, []);

  const fetchRecentSounds = async () => {
    const sounds = await getRecentSounds();
    setRecentSounds(Array.isArray(sounds) ? sounds : []);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSoundPick = (value) => {
    setFormData({ ...formData, sound: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const resetForm = () => {
    setFormData({ title: '', body: '', sound: '', image: null });
    setPreviewUrl(null);
    setExtraFields([]);
  };

  const addExtraField = () => setExtraFields([...extraFields, newExtraField()]);

  const removeExtraField = (id) => setExtraFields(extraFields.filter((f) => f.id !== id));

  const updateExtraField = (id, prop, value) =>
    setExtraFields(extraFields.map((f) => (f.id === id ? { ...f, [prop]: value } : f)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.title.trim() || !formData.body.trim()) {
      setError('Sarlavha va matn majburiy.');
      return;
    }

    setSending(true);
    try {
      const result = await pushNotification({ ...formData, data: extraFieldsToObject(extraFields) });
      setSuccess(
        `Yuborildi! Muvaffaqiyatli: ${result.success ?? 0}, Xato: ${result.failed ?? 0}`
      );
      resetForm();
      fetchRecentSounds();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Push-notification yuborishda xatolik yuz berdi.');
    } finally {
      setSending(false);
    }
  };

  const handleTestSend = async () => {
    setError(null);
    setSuccess(null);

    if (!testToken.trim()) {
      setError('Test uchun FCM tokenni kiriting.');
      return;
    }
    if (!formData.title.trim() || !formData.body.trim()) {
      setError('Sarlavha va matn majburiy.');
      return;
    }

    setTestSending(true);
    try {
      await testPushNotification({ ...formData, token: testToken.trim(), data: extraFieldsToObject(extraFields) });
      setSuccess('Test push shu tokenga yuborildi.');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Test push yuborishda xatolik yuz berdi.');
    } finally {
      setTestSending(false);
    }
  };

  return (
    <div className="bg-[#0f111a] min-h-screen p-4 sm:p-6 lg:p-8 lg:ml-64 text-white">
      <div className="max-w-6xl mx-auto">

        <div className="mb-8 pt-4">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-center tracking-tight text-blue-400">
            Push-notification yuborish
          </h1>
          <p className="text-gray-400 text-center mt-2">
            Barcha foydalanuvchilarga push-xabar yuboring.
          </p>
        </div>

        {success && (
          <div className="p-3 mb-6 bg-green-900/40 border border-green-600 rounded-lg text-green-300 text-center shadow-lg">
            {success}
          </div>
        )}
        {error && (
          <div className="p-3 mb-6 bg-red-900/40 border border-red-600 rounded-lg text-red-300 text-center shadow-lg">
            Xatolik: {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">

        <div className="bg-[#1c1e2c] p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-700">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6">

            {/* Sarlavha */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Sarlavha</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Masalan: Yangi serial qo'shildi!"
                className="block w-full p-3 bg-[#0f111a] border border-gray-600 rounded-lg text-white focus:ring-blue-500 focus:border-blue-500 shadow-inner"
                required
              />
            </div>

            {/* Matn */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Matn</label>
              <textarea
                name="body"
                value={formData.body}
                onChange={handleInputChange}
                rows={4}
                placeholder="Xabar matnini kiriting"
                className="block w-full p-3 bg-[#0f111a] border border-gray-600 rounded-lg text-white focus:ring-blue-500 focus:border-blue-500 shadow-inner resize-none"
                required
              />
            </div>

            {/* Sound */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Sound (ixtiyoriy)</label>
              <input
                type="text"
                name="sound"
                value={formData.sound}
                onChange={handleInputChange}
                placeholder="Masalan: notification_sound"
                className="block w-full p-3 bg-[#0f111a] border border-gray-600 rounded-lg text-white focus:ring-blue-500 focus:border-blue-500 shadow-inner"
              />
              {recentSounds.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Oldin yozilganlar:</p>
                  <div className="flex flex-wrap gap-2">
                    {recentSounds.map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => handleSoundPick(s)}
                        className={`px-3 py-1.5 text-xs rounded-full border transition
                          ${formData.sound === s
                            ? 'bg-blue-600 border-blue-500 text-white'
                            : 'bg-[#0f111a] border-gray-600 text-gray-300 hover:border-blue-500 hover:text-white'
                          }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Rasm */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Rasm (ixtiyoriy)</label>
              <input
                type="file"
                name="image"
                onChange={handleFileChange}
                accept="image/*"
                className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0 file:text-sm file:font-semibold
                  file:bg-indigo-600 file:text-white hover:file:bg-indigo-700 cursor-pointer
                  bg-[#0f111a] border border-gray-600 rounded-lg p-1.5
                "
              />
            </div>

            {previewUrl && (
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Rasm ko'rinishi</label>
                <div className="w-full h-auto max-h-80 overflow-hidden rounded-xl border-4 border-gray-700 shadow-xl bg-gray-900">
                  <img src={previewUrl} alt="Notification preview" className="w-full h-full object-contain" />
                </div>
              </div>
            )}

            {/* Qo'shimcha ma'lumotlar (data) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  Qo'shimcha ma'lumotlar (data)
                </label>
                <button
                  type="button"
                  onClick={addExtraField}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
                >
                  <Plus size={14} /> Qo'shish
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Ilova qabul qilishi kerak bo'lgan qo'shimcha key-value juftliklari (masalan: type, order_id, user_id).
              </p>

              {extraFields.length > 0 && (
                <div className="space-y-2">
                  {extraFields.map((field) => (
                    <div key={field.id} className="flex gap-2">
                      <input
                        type="text"
                        value={field.key}
                        onChange={(e) => updateExtraField(field.id, 'key', e.target.value)}
                        placeholder="key (masalan: order_id)"
                        className="flex-1 p-2.5 bg-[#0f111a] border border-gray-600 rounded-lg text-white text-sm focus:ring-indigo-500 focus:border-indigo-500 shadow-inner"
                      />
                      <input
                        type="text"
                        value={field.value}
                        onChange={(e) => updateExtraField(field.id, 'value', e.target.value)}
                        placeholder="value (masalan: 123)"
                        className="flex-1 p-2.5 bg-[#0f111a] border border-gray-600 rounded-lg text-white text-sm focus:ring-indigo-500 focus:border-indigo-500 shadow-inner"
                      />
                      <button
                        type="button"
                        onClick={() => removeExtraField(field.id)}
                        className="px-3 bg-red-600/80 text-white rounded-lg hover:bg-red-700 transition shrink-0"
                        aria-label="O'chirish"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Test token */}
            <div className="pt-2 border-t border-gray-700">
              <label className="block text-sm font-medium mb-2 text-gray-300">
                Test FCM token (ixtiyoriy)
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Barchaga yuborishdan oldin, faqat shu tokenga (masalan o'z telefoningizga) test push yuborib tekshirib ko'ring.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={testToken}
                  onChange={(e) => setTestToken(e.target.value)}
                  placeholder="FCM device token"
                  className="flex-1 p-3 bg-[#0f111a] border border-gray-600 rounded-lg text-white focus:ring-purple-500 focus:border-purple-500 shadow-inner"
                />
                <button
                  type="button"
                  onClick={handleTestSend}
                  disabled={testSending}
                  className="px-5 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition duration-300 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {testSending ? 'Yuborilmoqda...' : 'Test yuborish'}
                </button>
              </div>
            </div>

            {/* Tugma */}
            <div className="flex flex-wrap gap-4 pt-2">
              <button
                type="submit"
                disabled={sending}
                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition duration-300 shadow-lg min-w-[160px] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? 'Yuborilmoqda...' : 'Barchaga yuborish'}
              </button>
            </div>
          </form>
        </div>

        {/* Mobil ko'rinish preview */}
        <div className="bg-[#1c1e2c] p-6 rounded-xl shadow-2xl border border-gray-700 lg:sticky lg:top-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4 text-center">Mobilkada qanday ko'rinadi</h2>
          <NotificationPreview
            title={formData.title}
            body={formData.body}
            imageUrl={previewUrl}
          />
        </div>

        </div>
      </div>
    </div>
  );
};

export default SendNotification;
